import { type QueryKey } from '@tanstack/react-query';
import { t } from 'i18next';

import { useDataMutation } from '@/hooks/shared/useDataMutation';
import { IN_APP_NOTIFICATIONS_QUERY_KEY } from '@/hooks/inAppNotification/useInAppNotifications';
import { IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY } from '@/hooks/inAppNotification/useInAppNotificationsPage';
import { markInAppNotificationSeen } from '@/mutations/inAppNotification';
import { notification } from '@/utils/notification';
import { queryClient } from '@/resources/queryClient';
import {
  markInAppNotificationSeenInCache,
  markInAppNotificationsSeenInPage,
} from '@/utils/inAppNotification';
import type {
  InAppNotificationApiRecord,
  InAppNotificationsData,
  InAppNotificationsPageData,
} from '@/types/inAppNotification';

type OnMutateResult = {
  previous: InAppNotificationsData | undefined;
  previousPages: [QueryKey, InAppNotificationsPageData | undefined][];
};

/**
 * Marks one notification as seen, updating the cache before the request resolves.
 *
 * The row is flipped to read in place rather than dropped: the panel lists read
 * history alongside unread, so a notification the user opens stays visible.
 */
export const useMarkInAppNotificationSeen = () => {
  const { mutate, mutateAsync, ...rest } = useDataMutation<
    InAppNotificationApiRecord | undefined,
    number,
    unknown,
    OnMutateResult
  >(['markInAppNotificationSeen'], markInAppNotificationSeen, {
    onMutate: async (id) => {
      // An in-flight backfill would otherwise land after the optimistic write
      // and put the notification straight back.
      await queryClient.cancelQueries({ queryKey: IN_APP_NOTIFICATIONS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY });

      const previous = queryClient.getQueryData<InAppNotificationsData>(
        IN_APP_NOTIFICATIONS_QUERY_KEY,
      );
      const previousPages = queryClient.getQueriesData<InAppNotificationsPageData>({
        queryKey: IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY,
      });

      queryClient.setQueryData<InAppNotificationsData>(
        IN_APP_NOTIFICATIONS_QUERY_KEY,
        (current) => markInAppNotificationSeenInCache(current, id),
      );
      queryClient.setQueriesData<InAppNotificationsPageData>(
        { queryKey: IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY },
        (current) => markInAppNotificationsSeenInPage(current, id),
      );

      return { previous, previousPages };
    },
    onError: (_error, _id, onMutateResult) => {
      if (onMutateResult?.previous) {
        queryClient.setQueryData(IN_APP_NOTIFICATIONS_QUERY_KEY, onMutateResult.previous);
      }

      onMutateResult?.previousPages.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      notification.danger({ body: t('inAppNotification:errors.markSeen') });
    },
    // The response carries no counts, so reconcile with the server. Pages are
    // only marked stale, not refetched: under the unread filter a refetch would
    // pull the row out from under the cursor. It goes on the next fetch instead.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: IN_APP_NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: IN_APP_NOTIFICATIONS_PAGE_QUERY_KEY,
        refetchType: 'none',
      });
    },
  });

  return {
    markSeen: mutate,
    markSeenAsync: mutateAsync,
    ...rest,
  };
};
